import type { OrganizationMembership } from '@/lib/api/apiTypes';
import { authStore, useAuth } from '@/lib/auth/auth';
import { useOrgMemberships } from '@/features/workspaces/useOrgMemberships';
import { useWorkspaceMode } from '@/features/workspaces/useWorkspaceMode';
import { Platform } from 'react-native';
import * as React from 'react';
import * as SecureStore from 'expo-secure-store';
import { create } from 'zustand';

export type AccountContextKind = 'personal' | 'talent' | 'business';

export type AccountContext = {
  kind: AccountContextKind;
  activeOrgId: string | null;
  activeVenueId: string | null;
  activeTalentProfileId: string | null;
};

export type AvailableAccountContext =
  | {
      kind: 'personal';
      id: 'personal';
    }
  | {
      kind: 'business';
      id: string;
      membership: OrganizationMembership;
    };

type StoredAccountContext = {
  userID: string;
  context: AccountContext;
};

type AccountContextStore = {
  currentContext: AccountContext | null;
  isHydrating: boolean;
  hydratedUserID: string | null;
  hydrate: (userID: string | null) => Promise<void>;
  setContext: (userID: string, context: AccountContext) => Promise<void>;
  clear: () => Promise<void>;
};

const STORAGE_KEY = 'account_context';

function normalizeContext(input: Partial<AccountContext> | null | undefined): AccountContext | null {
  if (!input) {
    return null;
  }

  const kind = input.kind;
  if (kind !== 'personal' && kind !== 'talent' && kind !== 'business') {
    return null;
  }

  return {
    kind,
    activeOrgId: typeof input.activeOrgId === 'string' ? input.activeOrgId : null,
    activeVenueId: typeof input.activeVenueId === 'string' ? input.activeVenueId : null,
    activeTalentProfileId:
      typeof input.activeTalentProfileId === 'string' ? input.activeTalentProfileId : null,
  };
}

async function readStoredAccountContext(): Promise<StoredAccountContext | null> {
  try {
    const raw =
      Platform.OS === 'web'
        ? globalThis.localStorage?.getItem(STORAGE_KEY)
        : await SecureStore.getItemAsync(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredAccountContext>;
    if (typeof parsed.userID !== 'string') {
      return null;
    }

    const context = normalizeContext(parsed.context);
    if (!context) {
      return null;
    }

    return {
      userID: parsed.userID,
      context,
    };
  } catch {
    return null;
  }
}

async function writeStoredAccountContext(value: StoredAccountContext | null): Promise<void> {
  try {
    if (!value) {
      if (Platform.OS === 'web') {
        globalThis.localStorage?.removeItem(STORAGE_KEY);
        return;
      }

      await SecureStore.deleteItemAsync(STORAGE_KEY);
      return;
    }

    const raw = JSON.stringify(value);
    // Account context is not auth material; web keeps it persistent so business users land in the same mode.
    if (Platform.OS === 'web') {
      globalThis.localStorage?.setItem(STORAGE_KEY, raw);
      return;
    }

    await SecureStore.setItemAsync(STORAGE_KEY, raw);
  } catch {
    // Best-effort persistence only.
  }
}

function buildPersonalContext(): AccountContext {
  return {
    kind: 'personal',
    activeOrgId: null,
    activeVenueId: null,
    activeTalentProfileId: null,
  };
}

function buildBusinessContext(
  organizationID: string,
  venueID?: string | null
): AccountContext {
  return {
    kind: 'business',
    activeOrgId: organizationID,
    activeVenueId: venueID ?? null,
    activeTalentProfileId: null,
  };
}

const accountContextStore = create<AccountContextStore>((set) => ({
  currentContext: null,
  isHydrating: true,
  hydratedUserID: null,
  async hydrate(userID) {
    if (!userID) {
      set({
        currentContext: null,
        hydratedUserID: null,
        isHydrating: false,
      });
      return;
    }

    const stored = await readStoredAccountContext();
    set({
      currentContext: stored?.userID === userID ? stored.context : null,
      hydratedUserID: userID,
      isHydrating: false,
    });
  },
  async setContext(userID, context) {
    const normalized = normalizeContext(context);
    if (!normalized) {
      return;
    }

    set({
      currentContext: normalized,
      hydratedUserID: userID,
      isHydrating: false,
    });
    await writeStoredAccountContext({
      userID,
      context: normalized,
    });
  },
  async clear() {
    set({
      currentContext: null,
      hydratedUserID: null,
      isHydrating: false,
    });
    await writeStoredAccountContext(null);
  },
}));

export async function setBusinessContextForNotification(organizationID: string, venueID?: string | null) {
  const userID = authStore.getState().session?.me.user.id;
  if (!userID) {
    return;
  }

  await accountContextStore.getState().setContext(userID, buildBusinessContext(organizationID, venueID));
}

export function useAccountContext() {
  const session = useAuth((state) => state.session);
  const userID = session?.me.user.id ?? null;
  const currentContext = accountContextStore((state) => state.currentContext);
  const hydratedUserID = accountContextStore((state) => state.hydratedUserID);
  const isHydrating = accountContextStore((state) => state.isHydrating);
  const hydrate = accountContextStore((state) => state.hydrate);
  const setContext = accountContextStore((state) => state.setContext);
  const clear = accountContextStore((state) => state.clear);
  const workspaceMode = useWorkspaceMode();
  const membershipsQuery = useOrgMemberships(workspaceMode.supportsOrgWorkspace);

  React.useEffect(() => {
    if (hydratedUserID === userID && !isHydrating) {
      return;
    }

    void hydrate(userID);
  }, [hydrate, hydratedUserID, isHydrating, userID]);

  const businessMemberships = React.useMemo(
    () => (workspaceMode.orgAccessEnabled ? membershipsQuery.data?.memberships ?? [] : []),
    [membershipsQuery.data?.memberships, workspaceMode.orgAccessEnabled]
  );
  const isBusinessMembershipsPending =
    workspaceMode.orgAccessEnabled && (membershipsQuery.isPending || membershipsQuery.isFetching);

  const availableContexts = React.useMemo<AvailableAccountContext[]>(() => {
    if (!session) {
      return [];
    }

    const contexts: AvailableAccountContext[] = [{ kind: 'personal', id: 'personal' }];

    for (const membership of businessMemberships) {
      contexts.push({
        kind: 'business',
        id: membership.organization_id,
        membership,
      });
    }

    return contexts;
  }, [businessMemberships, session]);

  const resolvedContext = React.useMemo<AccountContext | null>(() => {
    if (!session) {
      return null;
    }

    if (!currentContext) {
      return buildPersonalContext();
    }

    if (currentContext.kind === 'business') {
      // Keep business context stable while memberships load, then fall back if access changed.
      if (isBusinessMembershipsPending) {
        return currentContext;
      }

      const matchingMembership = businessMemberships.find(
        (membership) => membership.organization_id === currentContext.activeOrgId
      );
      if (!matchingMembership) {
        return buildPersonalContext();
      }

      const matchingVenue =
        currentContext.activeVenueId &&
        matchingMembership.venues.some((venue) => venue.venue_id === currentContext.activeVenueId);

      return buildBusinessContext(
        matchingMembership.organization_id,
        matchingVenue ? currentContext.activeVenueId : matchingMembership.venues[0]?.venue_id
      );
    }

    if (currentContext.kind === 'talent') {
      return buildPersonalContext();
    }

    return buildPersonalContext();
  }, [businessMemberships, currentContext, isBusinessMembershipsPending, session]);

  React.useEffect(() => {
    if (isHydrating) {
      return;
    }

    if (!userID) {
      if (currentContext) {
        void clear();
      }
      return;
    }

    if (!resolvedContext) {
      return;
    }

    const contextChanged =
      !currentContext ||
      currentContext.kind !== resolvedContext.kind ||
      currentContext.activeOrgId !== resolvedContext.activeOrgId ||
      currentContext.activeVenueId !== resolvedContext.activeVenueId ||
      currentContext.activeTalentProfileId !== resolvedContext.activeTalentProfileId;

    if (contextChanged) {
      void setContext(userID, resolvedContext);
    }
  }, [clear, currentContext, isHydrating, resolvedContext, setContext, userID]);

  const setPersonalContext = React.useCallback(async () => {
    if (!userID) {
      return;
    }

    await setContext(userID, buildPersonalContext());
  }, [setContext, userID]);

  const setBusinessContext = React.useCallback(
    async (organizationID: string, venueID?: string | null) => {
      if (!userID) {
        return;
      }

      await setContext(userID, buildBusinessContext(organizationID, venueID));
    },
    [setContext, userID]
  );

  return {
    currentContext: resolvedContext,
    currentBusinessMembership:
      resolvedContext?.kind === 'business'
        ? businessMemberships.find(
            (membership) => membership.organization_id === resolvedContext.activeOrgId
          ) ?? null
        : null,
    availableContexts,
    meaningfulContextCount: availableContexts.length,
    hasPersonalContext: Boolean(session),
    hasTalentContext: false,
    hasBusinessContext: businessMemberships.length > 0,
    isHydrating,
    membershipsQuery,
    setBusinessContext,
    setPersonalContext,
  };
}
